import { motion, useInView } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import Images from '../Images';

// Counter Animation Hook
const useCounter = (end, duration = 2, shouldStart = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / (duration * 1000), 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
      setCount(Math.floor(end * easeOutQuart));

      if (percentage < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, shouldStart]);

  return count;
};

// Individual Stat Card Component
const StatCard = ({ number, label, suffix = "+", delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const count = useCounter(number, 2.5, isInView);

  // Format number with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay }}
      className="text-center"
    >
      <motion.div
        initial={{ scale: 0.5 }}
        animate={isInView ? { scale: 1 } : { scale: 0.5 }}
        transition={{ duration: 0.6, delay: delay + 0.2 }}
      >
        <h3 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#F7941D] mb-3">
          {formatNumber(count)}
          {count === number && suffix}
        </h3>
      </motion.div>
      <p className="text-xl md:text-2xl text-white font-medium">
        {label}
      </p>
    </motion.div>
  );
};

// Main Stats Section Component
const StatsSection = () => {
  const statsData = [
    { number: 50000, label: "Happy Clients", suffix: "+" },
    { number: 3000000, label: "Annual Transactions", suffix: "+" },
    { number: 6000000, label: "Annual Sms", suffix: "+" }
  ];

  return (
    <section className="relative flex items-end overflow-hidden px-6 lg:px-8 py-8">
      {/* Container with max-width and rounded corners */}
      <div className="relative w-full max-w-[86rem] min-h-[500px] lg:min-h-[600px] mx-auto rounded-[2rem] overflow-hidden flex items-end">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={Images.about}
            alt="Happy customers with shopping bags"
            className="w-full h-full object-cover"
          />
          {/* Dark Overlay - stronger than hero for better stat visibility */}
          <div className="absolute inset-0 bg-black/70"></div>
        </div>

        {/* Stats Grid - positioned at bottom */}
        <div className="relative z-10 px-6 lg:px-16 py-12 lg:py-16 w-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            {statsData.map((stat, index) => (
              <StatCard
                key={index}
                number={stat.number}
                label={stat.label}
                suffix={stat.suffix}
                delay={index * 0.2}
              />
            ))}
          </div>
        </div>

        {/* Optional: Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FF6B2C] to-transparent opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FF6B2C] to-transparent opacity-50"></div>
      </div>
    </section>
  );
};

export default StatsSection;